import React, { useState, useEffect, useMemo, useContext } from 'react';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// SERVICES
import { getAllNotifs, deleteNotif } from 'services/notificaitonService';

// COMPONENTS
import TableComponent from 'components/common/tableComponent';
import SearchBar from 'components/administration/searchBarComponent';

// ICONS
import { Eye, Trash, Check, X } from 'lucide-react';

function HistoryAnnouncements() {
    const { addToast } = useContext(ToastContext);
    const [announcements, setAnnouncements] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmingDelete, setConfirmingDelete] = useState(null); // ID de l'annonce à confirmer

    // Récupération des annonces
    const fetchNotifications = async () => {
        try {
            const response = await getAllNotifs();
            setAnnouncements(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des notifications :', error);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    // Demande de confirmation avant suppression
    const requestDeleteConfirmation = (notif_id) => {
        setConfirmingDelete(notif_id);
    };

    // Annulation de la suppression
    const cancelDelete = () => {
        setConfirmingDelete(null);
    };

    // Suppression confirmée d'une annonce
    const handleConfirmedDelete = async (notif_id) => {
        try {
            await deleteNotif(notif_id);
            await fetchNotifications();
            addToast(
                'Succès',
                'L\'annonce a été supprimée avec succès.',
                null,
                null,
                'success'
            );
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'annonce :', error);
            addToast(
                'Erreur',
                'Une erreur est survenue lors de la suppression de l\'annonce.',
                null,
                null,
                'error'
            );
        } finally {
            setConfirmingDelete(null); // Réinitialiser l'état de confirmation
        }
    };

    // Filtrer les annonces en fonction du terme de recherche
    const filteredAnnouncements = useMemo(() => {
        if (!searchTerm) {
            return announcements;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();
        return announcements.filter(
            (notif) =>
                (notif.NTF_CONTENT && notif.NTF_CONTENT.toLowerCase().includes(lowerSearchTerm)) ||
                (notif.NTF_AUTHOR && notif.NTF_AUTHOR.toLowerCase().includes(lowerSearchTerm))
        );
    }, [searchTerm, announcements]);

    // Définition des colonnes pour TableComponent
    const columns = [
        { key: 'content', label: 'Annonce', type: 'text' },
        { key: 'author', label: 'Auteur', type: 'text' },
        { key: 'date', label: 'Date', type: 'date' },
        { key: 'actions', label: 'Actions', type: 'action' },
    ];

    // Transformation des données pour TableComponent
    const tableRows = filteredAnnouncements.map(notif => {
        const isConfirming = confirmingDelete === notif.NTF_ID;
        let rowActions = [];

        if (isConfirming) {
            rowActions = [
                {
                    icon: Check,
                    variant: 'success',
                    onClick: () => handleConfirmedDelete(notif.NTF_ID),
                    label: 'Confirmer'
                },
                {
                    icon: X,
                    variant: 'error',
                    onClick: cancelDelete,
                    label: 'Annuler'
                }
            ];
        } else {
            if (notif.NTF_URL) {
                rowActions.push({
                    icon: Eye,
                    variant: 'info',
                    onClick: () => window.open(notif.NTF_URL, '_blank', 'noopener,noreferrer'),
                    label: "Voir l'annonce"
                });
            }
            rowActions.push({
                icon: Trash,
                variant: 'error',
                onClick: () => requestDeleteConfirmation(notif.NTF_ID),
                label: 'Supprimer'
            });
        }

        return {
            NTF_ID: notif.NTF_ID,
            content: notif.NTF_CONTENT,
            author: notif.NTF_AUTHOR,
            date: notif.NTF_DATE,
            actions: rowActions
        };
    });

    return (
        <div className="h-full flex flex-col">
            {/* Barre de recherche */}
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-center">
                <SearchBar
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher par annonce ou auteur..."
                />
            </div>

            {/* Annonces */}
            <div className="flex-1 min-h-0">
                <TableComponent
                    columns={columns}
                    rows={tableRows}
                    defaultSortConfig={{ key: 'date', direction: 'descending' }}
                    emptyText="Aucune annonce trouvée."
                />
            </div>
        </div>
    );
}

export default HistoryAnnouncements;